import * as Sentry from '@sentry/react';

/**
 * Install global handlers for uncaught errors and unhandled Promise
 * rejections. Without these, async errors that bubble up past every
 * try/catch (and synchronous errors outside React's render tree) are
 * silently logged to the browser console — invisible to Sentry and to
 * the support team.
 *
 * What we listen to:
 * - `window.onerror` — catches uncaught synchronous errors and
 *   `throw new Error(...)` outside of React render (e.g. in event
 *   handlers, setTimeout callbacks, third-party libs).
 * - `window.onunhandledrejection` — catches Promise rejections that no
 *   .catch() handled (e.g. fetch failures that nobody awaited).
 *
 * Both forward to Sentry with a tag so the Sentry dashboard can
 * distinguish "uncaught window error" from "caught by ErrorBoundary".
 *
 * Idempotent: calling installGlobalErrorHandler() more than once is a
 * no-op (we tag the window object so we don't add duplicate listeners
 * during HMR).
 *
 * Safe to call before Sentry is initialized (Sentry.captureException
 * will be a no-op until Sentry.init is called).
 */
const INSTALLED_KEY = '__presensiGlobalErrorHandlerInstalled';

export const installGlobalErrorHandler = () => {
  if (typeof window === 'undefined') return;
  if (window[INSTALLED_KEY]) return;
  window[INSTALLED_KEY] = true;

  window.addEventListener('error', (event) => {
    // Some browsers fire `error` events for non-script resources
    // (images, iframes). We only care about real JS errors which have
    // a non-empty `message`.
    const error = event.error || (event.message ? new Error(event.message) : null);
    if (!error) return;

    Sentry.withScope((scope) => {
      scope.setTag('source', 'window.error');
      scope.setExtra('filename', event.filename);
      scope.setExtra('lineno', event.lineno);
      scope.setExtra('colno', event.colno);
      Sentry.captureException(error);
    });

    // eslint-disable-next-line no-console
    console.error('[window.error]', error);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const error =
      reason instanceof Error
        ? reason
        : new Error(typeof reason === 'string' ? reason : 'Unhandled promise rejection');

    Sentry.withScope((scope) => {
      scope.setTag('source', 'unhandledrejection');
      Sentry.captureException(error);
    });

    // eslint-disable-next-line no-console
    console.error('[unhandledrejection]', reason);
  });
};

export default installGlobalErrorHandler;
